import type { TokenCreateTxParams } from '@ardrive/turbo-sdk';
import {
  type Connection,
  PublicKey,
  SystemProgram,
  Transaction,
  TransactionInstruction,
} from '@solana/web3.js';
import bs58 from 'bs58';
import { isBlockhashStaleError, MAX_BLOCKHASH_RETRIES } from './blockhash';
import { getErrorMessage, isUserRejectedError } from './errorHandling';
import type { WalletAdapter } from './upload-providers/types';

const MEMO_PROGRAM_ID = 'MemoSq4gqABAXKb96qnH8TysNcWxMyWCqXgDLGmfcHr';

type RetryDetails = {
  attempt: number;
  errorMessage: string;
  usedWalletSendTransaction: boolean;
};

type CreateAndSubmitArDriveTopUpTxOptions = Pick<
  TokenCreateTxParams,
  'target' | 'tokenAmount' | 'turboCreditDestinationAddress'
> & {
  connection: Connection;
  walletAdapter: WalletAdapter;
  getLatestBlockhash: () => Promise<{
    blockhash: string;
    lastValidBlockHeight?: number;
  }>;
  sendRawTransaction: (serializedTx: Uint8Array) => Promise<string>;
  onRetry?: (details: RetryDetails) => void;
};

const getWalletPublicKey = (walletAdapter: WalletAdapter) =>
  new PublicKey(
    walletAdapter.publicKey.toBase58?.() ||
      bs58.encode(walletAdapter.publicKey.toBuffer()),
  );

const buildTopUpTransaction = ({
  feePayer,
  target,
  tokenAmount,
  turboCreditDestinationAddress,
}: Pick<
  CreateAndSubmitArDriveTopUpTxOptions,
  'target' | 'tokenAmount' | 'turboCreditDestinationAddress'
> & {
  feePayer: PublicKey;
}) => {
  const tx = new Transaction().add(
    SystemProgram.transfer({
      fromPubkey: feePayer,
      toPubkey: new PublicKey(target),
      lamports: BigInt(tokenAmount.toString()),
    }),
  );

  tx.feePayer = feePayer;

  if (turboCreditDestinationAddress) {
    tx.add(
      new TransactionInstruction({
        programId: new PublicKey(MEMO_PROGRAM_ID),
        keys: [],
        data: Buffer.from(
          `turboCreditDestinationAddress=${turboCreditDestinationAddress}`,
        ),
      }),
    );
  }

  return tx;
};

export async function createAndSubmitArDriveTopUpTx(
  options: CreateAndSubmitArDriveTopUpTxOptions,
): Promise<{ id: string; target: string }> {
  const feePayer = getWalletPublicKey(options.walletAdapter);

  for (let attempt = 1; attempt <= MAX_BLOCKHASH_RETRIES; attempt++) {
    const tx = buildTopUpTransaction({
      feePayer,
      target: options.target,
      tokenAmount: options.tokenAmount,
      turboCreditDestinationAddress: options.turboCreditDestinationAddress,
    });
    const usedWalletSendTransaction = Boolean(
      options.walletAdapter.sendTransaction,
    );

    try {
      if (options.walletAdapter.sendTransaction) {
        const id = await options.walletAdapter.sendTransaction(
          tx,
          options.connection,
        );
        return { id, target: options.target };
      }

      if (!options.walletAdapter.signTransaction) {
        throw new Error(
          'Wallet adapter must support signTransaction or sendTransaction',
        );
      }

      const { blockhash, lastValidBlockHeight } =
        await options.getLatestBlockhash();

      tx.recentBlockhash = blockhash;
      if (lastValidBlockHeight !== undefined) {
        tx.lastValidBlockHeight = lastValidBlockHeight;
      }

      const signedTx = await options.walletAdapter.signTransaction(tx);
      const id = await options.sendRawTransaction(signedTx.serialize());
      return { id, target: options.target };
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      if (
        isUserRejectedError(error) ||
        !isBlockhashStaleError(errorMessage) ||
        attempt >= MAX_BLOCKHASH_RETRIES
      ) {
        throw error;
      }

      options.onRetry?.({
        attempt,
        errorMessage,
        usedWalletSendTransaction,
      });
    }
  }

  throw new Error('Failed to submit ArDrive top-up transaction');
}
